/**
 * CalibrationGraphTab - Highcharts scatter plot with both trend curves
 * (logarithmic + linear), R-values, curve picker with recommendation, and
 * user-configurable trend-line extension percentages.
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  CircularProgress,
  Alert,
  Button,
  Chip,
  Snackbar,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  InputAdornment,
} from '@mui/material';
import { Refresh as RefreshIcon, Calculate as CalculateIcon } from '@mui/icons-material';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import calibrationsService, { GraphData, CurveType } from '../services/calibrations';
import { strengthFromEa, rValue } from '../utils/regressionConversion';
import {
  MATURITY_AXIS_UNKNOWN,
  knownMaturityMeta,
  labelWithUnit,
  maturityShortLabel,
} from '../utils/maturityDisplay';

interface CalibrationGraphTabProps {
  calId: string;
  /** No edit permission, or the calibration is finished: curve choice and extension are fixed. */
  readOnly?: boolean;
  /** Recalculate is an edit action. On a finished calibration it only backfills missing values. */
  canRecalculate?: boolean;
  /** Calibration page panel: trend choice and "Extend start" only, as in the mockup. */
  compact?: boolean;
  /** 'Nurse Saul' or 'Arrhenius' from the calibration's maturity basis;
   *  unknown for a Manual calibration without one. */
  maturityMethod?: string | null;
  /** The basis's specified temperature (°C), named on an Arrhenius axis;
   *  20 °C when not given. */
  specifiedTemp?: number | null;
  /** The company strength unit (MPa / Psi / N/mm²); plain "Strength"
   *  without a unit while it is unknown. */
  strengthUnit?: string | null;
}

const CURVE_STEPS = 100;

const fmtCoef = (n: number): string => n.toFixed(2);

const formulaText = (a: number, b: number, curve: CurveType): string => {
  const sign = b < 0 ? '−' : '+';
  const term = curve === 'linear' ? 'x' : 'ln(x)';
  return `y = ${fmtCoef(a)} ${sign} ${fmtCoef(Math.abs(b))} × ${term}`;
};

const rText = (r2: number | null | undefined): string => {
  const r = rValue(r2);
  return r != null ? `R = ${r.toFixed(2)}` : '';
};

/** Curve points across [minEa, maxEa]; log curves step in ln-space. Points
 * with negative strength are dropped (linear curves can cross zero). */
const buildCurve = (
  a: number,
  b: number,
  minEa: number,
  maxEa: number,
  curve: CurveType,
): number[][] => {
  if (!(minEa > 0) && curve === 'log') minEa = Math.min(1e-6, maxEa / 1000);
  const points: number[][] = [];
  for (let i = 0; i <= CURVE_STEPS; i++) {
    let ea: number;
    if (curve === 'log') {
      const logMin = Math.log(minEa);
      const logMax = Math.log(maxEa);
      ea = Math.exp(logMin + (i * (logMax - logMin)) / CURVE_STEPS);
    } else {
      ea = minEa + (i * (maxEa - minEa)) / CURVE_STEPS;
    }
    const strength = strengthFromEa(ea, a, b, curve);
    if (strength != null && strength >= 0) {
      points.push([parseFloat(ea.toFixed(2)), parseFloat(strength.toFixed(2))]);
    }
  }
  return points;
};

const CalibrationGraphTab: React.FC<CalibrationGraphTabProps> = ({
  calId,
  readOnly = false,
  canRecalculate = true,
  compact = false,
  maturityMethod,
  specifiedTemp,
  strengthUnit,
}) => {
  // Nurse-Saul maturity is a temperature-time factor in °C·hrs; every other
  // method is an equivalent age in hrs at the specified temperature. A
  // Manual calibration without a basis does not record which.
  const basisMeta = knownMaturityMeta(maturityMethod, specifiedTemp);
  const maturityAxis = basisMeta
    ? { title: basisMeta.axis_title, short: maturityShortLabel(basisMeta), unit: basisMeta.unit }
    : { title: MATURITY_AXIS_UNKNOWN, short: 'Maturity', unit: '' };
  const sUnit = strengthUnit?.trim() || '';
  const [graphData, setGraphData] = useState<GraphData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState<{ msg: string; severity: 'success' | 'error' } | null>(null);
  const [startPct, setStartPct] = useState<string>('25');
  const [endPct, setEndPct] = useState<string>('25');

  const loadGraph = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await calibrationsService.getGraphData(calId);
      setGraphData(data);
      setStartPct(String(data.extend_start_pct));
      setEndPct(String(data.extend_end_pct));
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to load graph data');
    } finally {
      setLoading(false);
    }
  }, [calId]);

  useEffect(() => {
    loadGraph();
  }, [loadGraph]);

  const handleCurveChange = async (_: unknown, value: CurveType | null) => {
    if (!value || !graphData || value === graphData.curve_type) return;
    setSaving(true);
    try {
      // Save the choice first — this also enforces the finished/locked rule
      // (409) before anything else is written.
      await calibrationsService.updateCurveSettings(calId, { curve_type: value });
      // Backfill both fits + R-values if this calibration predates the
      // curve-comparison feature (choice is already marked manual, so the
      // recalculation cannot override it).
      const fitsMissing =
        graphData.linear_a == null || graphData.r2_log == null || graphData.r2_linear == null;
      if (fitsMissing) {
        try {
          await calibrationsService.calculateRegression(calId);
        } catch {
          // Not enough valid samples to fit — the selection is still saved;
          // the banner keeps offering Recalculate once samples exist.
        }
      }
      setSnackbar({
        msg: value === 'linear' ? 'Linear trend line selected' : 'Logarithmic trend line selected',
        severity: 'success',
      });
      await loadGraph();
    } catch (err: any) {
      setSnackbar({
        msg: err.response?.data?.detail || 'Failed to update curve selection',
        severity: 'error',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleRecalculate = async () => {
    setSaving(true);
    try {
      const result = await calibrationsService.calculateRegression(calId);
      setSnackbar({
        msg: `Regression recalculated. Recommended: ${
          result.recommended_curve === 'linear' ? 'Linear' : 'Logarithmic'
        }`,
        severity: 'success',
      });
      await loadGraph();
    } catch (err: any) {
      setSnackbar({
        msg: err.response?.data?.detail || 'Failed to recalculate regression',
        severity: 'error',
      });
    } finally {
      setSaving(false);
    }
  };

  // Auto-save the extension fields on blur/Enter; no separate Apply step.
  const handleExtensionSave = async () => {
    if (!graphData) return;
    const start = Number(startPct);
    const end = Number(endPct);
    if (!Number.isFinite(start) || !Number.isFinite(end) || start < 0 || end < 0 || start > 500 || end > 500) {
      setSnackbar({ msg: 'Extension must be between 0 and 500 %', severity: 'error' });
      return;
    }
    // Nothing changed — don't hit the server on every focus change.
    if (start === graphData.extend_start_pct && end === graphData.extend_end_pct) return;
    setSaving(true);
    try {
      await calibrationsService.updateCurveSettings(calId, {
        extend_start_pct: start,
        extend_end_pct: end,
      });
      setSnackbar({ msg: 'Trend line extension saved', severity: 'success' });
      await loadGraph();
    } catch (err: any) {
      setSnackbar({
        msg: err.response?.data?.detail || 'Failed to save extension',
        severity: 'error',
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  if (!graphData || graphData.samples.length === 0) {
    return (
      <Alert severity="info">
        No valid sample data available for graphing. Add samples with both maturity and strength values.
      </Alert>
    );
  }

  const {
    samples,
    regression_a,
    regression_b,
    linear_a,
    linear_b,
    r2_log,
    r2_linear,
    curve_type,
    recommended_curve,
    min_ea,
    max_ea,
  } = graphData;

  const hasRange = min_ea != null && max_ea != null && max_ea > 0;
  const hasLog = regression_a != null && regression_b != null && hasRange;
  const hasLinear = linear_a != null && linear_b != null && hasRange;
  const chosen: CurveType = curve_type;

  const curveSeries: Highcharts.SeriesOptionsType[] = [];
  const addCurveSeries = (curve: CurveType, a: number, b: number, r2: number | null | undefined) => {
    const isChosen = chosen === curve;
    const label = curve === 'log' ? 'Logarithmic' : 'Linear';
    const parts = [`${label}: ${formulaText(a, b, curve)}`, rText(r2)].filter(Boolean);
    curveSeries.push({
      name: parts.join('  '),
      type: 'line',
      data: buildCurve(a, b, min_ea!, max_ea!, curve),
      marker: { enabled: false },
      color: isChosen ? '#d32f2f' : '#9e9e9e',
      dashStyle: (isChosen ? 'Solid' : 'ShortDot') as Highcharts.DashStyleValue,
      lineWidth: isChosen ? 2.5 : 1.5,
      opacity: isChosen ? 1 : 0.7,
      enableMouseTracking: false,
      zIndex: isChosen ? 2 : 1,
    });
  };
  // Chosen curve added last so it renders on top
  if (hasLog && chosen !== 'log') addCurveSeries('log', regression_a!, regression_b!, r2_log);
  if (hasLinear && chosen !== 'linear') addCurveSeries('linear', linear_a!, linear_b!, r2_linear);
  if (hasLog && chosen === 'log') addCurveSeries('log', regression_a!, regression_b!, r2_log);
  if (hasLinear && chosen === 'linear') addCurveSeries('linear', linear_a!, linear_b!, r2_linear);

  const chosenR2 = chosen === 'linear' ? r2_linear : r2_log;
  const chosenFit = chosen === 'linear' ? hasLinear : hasLog;
  // Pre-feature calibrations have log A/B but no linear fit / R² until the
  // regression is recalculated.
  const needsRecalc = hasLog && (linear_a == null || r2_log == null);

  const subtitleText = (() => {
    if (chosenFit) {
      return `${chosen === 'linear' ? 'Linear' : 'Logarithmic'} trend  ·  ${formulaText(
        chosen === 'linear' ? linear_a! : regression_a!,
        chosen === 'linear' ? linear_b! : regression_b!,
        chosen,
      )}${rText(chosenR2) ? `  ·  ${rText(chosenR2)}` : ''}`;
    }
    if (hasLog) {
      // Chosen curve not computed yet — show the fit that does exist.
      return `${chosen === 'linear' ? 'Linear' : 'Logarithmic'} trend not calculated yet  ·  showing Logarithmic: ${formulaText(regression_a!, regression_b!, 'log')}`;
    }
    return 'No regression calculated yet';
  })();

  const options: Highcharts.Options = {
    chart: {
      type: 'scatter',
      height: 500,
    },
    title: {
      text: 'Calibration Regression',
    },
    subtitle: { text: subtitleText },
    xAxis: {
      title: { text: maturityAxis.title },
      min: 0,
    },
    yAxis: {
      title: { text: labelWithUnit('Strength', sUnit) },
    },
    tooltip: {
      pointFormat:
        `${maturityAxis.short}: <b>{point.x}</b>${maturityAxis.unit ? ` ${maturityAxis.unit}` : ''}` +
        `<br/>Strength: <b>{point.y}</b>${sUnit ? ` ${sUnit}` : ''}`,
    },
    legend: {
      enabled: true,
    },
    credits: {
      enabled: false,
    },
    accessibility: {
      description: `Calibration samples with logarithmic and linear trend curves of concrete strength over ${
        basisMeta?.index_label || 'maturity'
      }`,
    },
    series: [
      {
        name: 'Samples',
        type: 'scatter',
        data: samples.map(([ea, str]) => [ea, str]),
        marker: {
          radius: 6,
          symbol: 'circle',
        },
        color: '#1976d2',
        zIndex: 3,
      },
      ...curveSeries,
    ],
  };

  const recommendedLabel =
    recommended_curve === 'linear' ? 'Linear' : recommended_curve === 'log' ? 'Logarithmic' : null;

  if (compact) {
    const r2 = chosenR2;
    return (
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1, flexWrap: 'wrap' }}>
          <ToggleButtonGroup size="small" exclusive value={chosen} onChange={handleCurveChange} disabled={saving || readOnly}>
            <ToggleButton value="log" sx={{ textTransform: 'none', px: 1.5, py: 0.25 }}>Logarithmic</ToggleButton>
            <ToggleButton value="linear" sx={{ textTransform: 'none', px: 1.5, py: 0.25 }}>Linear</ToggleButton>
          </ToggleButtonGroup>
          <Box sx={{ flexGrow: 1 }} />
          <TextField
            size="small"
            label="Extend start"
            type="number"
            value={startPct}
            onChange={(e) => setStartPct(e.target.value)}
            onBlur={handleExtensionSave}
            onKeyDown={(e) => e.key === 'Enter' && (e.target as HTMLInputElement).blur()}
            disabled={saving || readOnly}
            sx={{ width: 110 }}
            InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
            inputProps={{ min: 0, max: 500 }}
          />
        </Box>
        {needsRecalc && canRecalculate && (
          <Alert severity="info" sx={{ mb: 1, py: 0 }} action={
            <Button size="small" onClick={handleRecalculate} disabled={saving}>Recalculate</Button>
          }>
            Fit both trend lines from the results.
          </Alert>
        )}
        <HighchartsReact
          highcharts={Highcharts}
          options={{
            ...options,
            chart: { ...(options.chart as object), height: 300 },
            // The panel already names it; the formula and R² sit in the caption.
            title: { text: undefined },
            subtitle: { text: undefined },
            legend: { enabled: false },
          }}
        />
        <Typography variant="caption" color="text.secondary">
          {chosen === 'linear' ? 'Linear' : 'Logarithmic'}
          {r2 != null ? ` · R² ${r2.toFixed(2)}` : ''}
          {recommendedLabel && recommended_curve !== chosen ? ` · ${recommendedLabel} fits better` : ''}
        </Typography>
        <Snackbar open={snackbar !== null} autoHideDuration={4000} onClose={() => setSnackbar(null)}>
          <Alert severity={snackbar?.severity ?? 'success'} onClose={() => setSnackbar(null)} variant="filled">
            {snackbar?.msg}
          </Alert>
        </Snackbar>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Trend line:
          </Typography>
          <ToggleButtonGroup
            size="small"
            exclusive
            value={chosen}
            onChange={handleCurveChange}
            disabled={saving || readOnly}
          >
            <ToggleButton value="log" sx={{ textTransform: 'none', px: 2 }}>
              Logarithmic
              {recommended_curve === 'log' && (
                <Chip label="Recommended" size="small" color="success" sx={{ ml: 1, height: 18, fontSize: '0.65rem' }} />
              )}
            </ToggleButton>
            <ToggleButton value="linear" sx={{ textTransform: 'none', px: 2 }}>
              Linear
              {recommended_curve === 'linear' && (
                <Chip label="Recommended" size="small" color="success" sx={{ ml: 1, height: 18, fontSize: '0.65rem' }} />
              )}
            </ToggleButton>
          </ToggleButtonGroup>
          {recommendedLabel && recommended_curve !== chosen && (
            <Tooltip title={`The ${recommendedLabel} curve has the higher R-value for this data`}>
              <Typography variant="caption" color="warning.main">
                {recommendedLabel} scores higher
              </Typography>
            </Tooltip>
          )}
        </Box>
        <Box sx={{ flexGrow: 1 }} />
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <TextField
            size="small"
            label="Extend start"
            type="number"
            value={startPct}
            onChange={(e) => setStartPct(e.target.value)}
            onBlur={handleExtensionSave}
            onKeyDown={(e) => e.key === 'Enter' && (e.target as HTMLInputElement).blur()}
            disabled={saving || readOnly}
            sx={{ width: 120 }}
            InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
            inputProps={{ min: 0, max: 500 }}
          />
          <TextField
            size="small"
            label="Extend end"
            type="number"
            value={endPct}
            onChange={(e) => setEndPct(e.target.value)}
            onBlur={handleExtensionSave}
            onKeyDown={(e) => e.key === 'Enter' && (e.target as HTMLInputElement).blur()}
            disabled={saving || readOnly}
            sx={{ width: 120 }}
            InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
            inputProps={{ min: 0, max: 500 }}
          />
          <Button
            size="small"
            variant="outlined"
            startIcon={<CalculateIcon />}
            onClick={handleRecalculate}
            disabled={saving || !canRecalculate}
            sx={{ textTransform: 'none' }}
          >
            Recalculate
          </Button>
          <Button size="small" startIcon={<RefreshIcon />} onClick={loadGraph} disabled={saving}>
            Refresh
          </Button>
        </Box>
      </Box>
      {needsRecalc && (
        <Alert severity="info" sx={{ mb: 1 }}>
          This calibration was calculated before trend-curve comparison existed. Click{' '}
          <b>Recalculate</b> to compute both trend lines and their R-values from the samples
          (the stored logarithmic coefficients will not change).
        </Alert>
      )}
      <HighchartsReact highcharts={Highcharts} options={options} />
      <Snackbar
        open={snackbar !== null}
        autoHideDuration={4000}
        onClose={() => setSnackbar(null)}
      >
        <Alert
          severity={snackbar?.severity ?? 'success'}
          onClose={() => setSnackbar(null)}
          variant="filled"
        >
          {snackbar?.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CalibrationGraphTab;
